import React from 'react';
import type { PageContext } from 'vike/types';
import { createCustomComponentLoader } from '@mutiny-pkg/bin-ui/src/customComponents/customComponentLoader';
import {
  registerHostRuntime,
  type LoadedCustomComponentModule,
} from '@mutiny-pkg/bin-ui/src/customComponents/hostRuntime';
import { buildRuntimeShimUrl } from '@mutiny-pkg/bin-ui/src/customComponents/runtimeShimUrl';
import { ensureRuntimeLibrariesRegistered } from '@mutiny-pkg/bin-ui/src/customComponents/runtimeLibraryModules';
import type { FlexiblePageInput } from '@/pages/types';
import {
  createComponentStateRuntime,
  createHttpComponentStateClient,
  createMemoryComponentStateClient,
} from '@mutiny-pkg/bin-ui/src/customComponents/componentStateRuntime';

/**
 * Pre-fetches every artifact the page references BEFORE `hydrateRoot`, so the first React render
 * is sync-matching-SSR — no Suspense throw, no React-18 dehydration downgrade. Blocks interactivity
 * by the slowest load; first paint (SSR'd HTML) is unaffected.
 */
export async function preloadCustomComponentsClient(pageContext: PageContext): Promise<void> {
  const data = pageContext.data as FlexiblePageInput | undefined;
  if (!data) return;

  // Custom components hydrate in the export browser, where `motion/react` would replay its entrance
  // animations and the screenshot would catch them mid-flight (half-drawn chart). Skip motion so it
  // paints final state instantly — client analog of the SSR `renderMotionAtRest` path.
  if (data.isPdfExport) {
    const { MotionGlobalConfig } = await import('motion/react');
    MotionGlobalConfig.skipAnimations = true;
  }

  const artifacts = data.customComponents?.artifacts ?? {};
  const slugToCurrentSourceHash = data.customComponents?.slugToCurrentSourceHash ?? {};
  const sourceHashes = Object.keys(artifacts);
  if (sourceHashes.length === 0) return;
  const staticSiteDomain = data.staticSiteDomain;
  if (!staticSiteDomain) return;

  const resolveCompiledUrl = (sourceHash: string): string => `https://${staticSiteDomain}/assets/${sourceHash}.js`;
  const resolveRuntimeUrl = (specifier: string): string => buildRuntimeShimUrl(staticSiteDomain, specifier);

  const loader = createCustomComponentLoader({
    resolveCompiledUrl,
    resolveRuntimeUrl,
    getManifest: () => ({ slugToCurrentSourceHash }),
    ensureRuntimeLibraries: ensureRuntimeLibrariesRegistered,
  });
  const { componentStateEndpoint, componentStateCapability } = data.clientData;
  const stateClient =
    componentStateEndpoint && componentStateCapability
      ? createHttpComponentStateClient({
          endpoint: componentStateEndpoint,
          capability: componentStateCapability,
          defaultOptOutPiiTracking: data.clientData.defaultOptOutPiiTracking,
        })
      : createMemoryComponentStateClient();

  // Artifacts read `globalThis.__mutinyHostRuntime` synchronously during their shim import — must
  // be registered before any `loadByHash` call.
  registerHostRuntime({
    React,
    loadComponent: (slug) => loader.loadBySlug(slug),
    state: createComponentStateRuntime(stateClient),
  });

  const entries = await Promise.all(
    sourceHashes.map(async (sourceHash): Promise<[string, LoadedCustomComponentModule] | null> => {
      try {
        return [sourceHash, await loader.loadByHash(sourceHash)];
      } catch {
        return null;
      }
    }),
  );

  const preloaded = new Map<string, LoadedCustomComponentModule>();
  for (const entry of entries) {
    if (entry) preloaded.set(entry[0], entry[1]);
  }
  pageContext.preloadedCustomComponents = preloaded;
}
